import { addMonths, endOfDay, startOfDay, subDays } from "date-fns";
import { defineStore } from "pinia";

const API_NAME = "rota";
const API_VERSION = "/v1";

export const useRotaStore = defineStore(
  "rotas",
  () => {
    /**
     * Fetch non-draft rotas for the given period
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @returns array
     */
    const getRotasAndShiftsForPeriod = async function (startDate, endDate) {
      const myInit = {};
      myInit.queryStringParameters = {
        filter: JSON.stringify([
          {
            subject: "startDate",
            comparison: "lte",
            value: endDate.toISOString(),
          },
          {
            subject: "endDate",
            comparison: "gte",
            value: startDate.toISOString(),
          },
        ]),
        publishedOnly: true,
      };
      const response = await this.$api.get(
        API_NAME,
        `${API_VERSION}/rotas`,
        myInit,
      );

      if (response?._embedded.rotas === undefined) {
        return [];
      }
      const promises = [];
      for (const rota of response._embedded.rotas) {
        promises.push(getRota.call(this, rota.id));
      }
      return Promise.all(promises);
    };

    const getRota = async function (id) {
      return this.$api.get(API_NAME, `${API_VERSION}/rotas/${id}`);
    };

    const getRotas = async function () {
      const myInit = {};
      myInit.queryStringParameters = {
        pageSize: 50,
        sort: JSON.stringify({
          ["_id"]: -1,
        }),
      };
      const response = await this.$api.get(
        API_NAME,
        `${API_VERSION}/rotas/`,
        myInit,
      );
      if (response?._embedded.rotas === undefined) {
        return [];
      }
      return response._embedded.rotas;
    };

    const getRotasExcludingLocked = async function () {
      const myInit = {};
      myInit.queryStringParameters = {
        pageSize: 50,
        sort: JSON.stringify({
          ["_id"]: -1,
        }),
        filter: JSON.stringify([
          {
            subject: "status",
            comparison: "in",
            value: ["published", "draft"],
          },
        ]),
      };
      const response = await this.$api.get(
        API_NAME,
        `${API_VERSION}/rotas/`,
        myInit,
      );
      if (response?._embedded.rotas === undefined) {
        return [];
      }
      return response._embedded.rotas;
    };

    const submitCoverRequest = async function (rotaId, shiftId, payload = {}) {
      const myInit = {
        body: payload,
      };

      return this.$api
        .post(
          API_NAME,
          `${API_VERSION}/rotas/${rotaId}/shifts/${shiftId}/cover/request`,
          myInit,
        )
        .then((response) => {
          return response;
        });
    };
    const submitCoverVolunteer = async function (rotaId, shiftId) {
      return this.$api
        .post(
          API_NAME,
          `${API_VERSION}/rotas/${rotaId}/shifts/${shiftId}/cover/volunteer`,
        )
        .then((response) => {
          return response;
        });
    };
    /**
     * Note, this endpoint performs different actions based on whether the user
     * is the shift owner or the cover requester etc. See CC-2400 for deatils.
     *
     * @param {*} rotaId
     * @param {*} shiftId
     * @returns {} updated shift object
     */
    const rejectCoverRequest = async function (rotaId, shiftId, userId = null) {
      const myInit = {};
      if (userId != null) {
        myInit.body = { userId: userId };
      }
      return this.$api
        .post(
          API_NAME,
          `${API_VERSION}/rotas/${rotaId}/shifts/${shiftId}/cover/reject`,
          myInit,
        )
        .then((response) => {
          return response;
        });
    };
    /**
     * Note, this endpoint performs different actions based on whether the user
     * is the shift owner or the cover requester etc. See CC-2400 for deatils.
     *
     * @param {*} rotaId
     * @param {*} shiftId
     * @param {*} userId Optional user ID of the volunteer to approve (if the approver is not the requester)
     * @returns {} updated shift object
     */
    const approveCoverRequest = async function (
      rotaId,
      shiftId,
      userId = null,
    ) {
      const myInit = {};
      if (userId) {
        myInit.body = { userId: userId };
      }
      return this.$api
        .post(
          API_NAME,
          `${API_VERSION}/rotas/${rotaId}/shifts/${shiftId}/cover/approve`,
          myInit,
        )
        .then((response) => {
          return response;
        });
    };
    /**
     *
     * @param {*} rotaId
     * @param {*} shiftId
     * @param {*} userId User ID of the volunteer to decline
     * @returns {} updated shift object
     */
    const declineCoverRequest = async function (rotaId, shiftId, userId) {
      const myInit = {};
      myInit.body = { userId: userId };
      return this.$api
        .post(
          API_NAME,
          `${API_VERSION}/rotas/${rotaId}/shifts/${shiftId}/cover/decline`,
          myInit,
        )
        .then((response) => {
          return response;
        });
    };
    /**
     * Fetches shift cover requests for a given date range.
     * Defaults to the last 7 days for startDate and 2 months from now for endDate.
     *
     * @param {*} payload
     * @returns
     */
    const getShiftCoverRequests = async function (payload) {
      const myInit = {
        queryStringParameters: {
          startDate: payload?.startDate ?? startOfDay(subDays(new Date(), 7)),
          endDate: payload?.endDate ?? endOfDay(addMonths(new Date(), 2)),
        },
      };
      return this.$api
        .doNotCatchIfError()
        .get(API_NAME, `${API_VERSION}/rotas/shifts/cover-requests`, myInit)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          if (
            (error.response && error.response.status === 404) ||
            error.message === "No shifts found"
          ) {
            return [];
          }
          throw error;
        });
    };

    /**
     * Fetches currently available shifts (for cover requets)
     *
     * @returns array
     */
    const getAvailableShifts = async function () {
      return this.$api
        .get(API_NAME, `${API_VERSION}/rotas/shifts/available`)
        .then((response) => {
          return response;
        });
    };

    const getCurrentRotas = async function () {
      return this.$api
        .get(API_NAME, `${API_VERSION}/rotas/current`)
        .then((response) => {
          if (response?._embedded?.rotas) {
            return response._embedded.rotas;
          }
          return [];
        });
    };

    return {
      submitCoverRequest,
      submitCoverVolunteer,
      rejectCoverRequest,
      approveCoverRequest,
      declineCoverRequest,
      getShiftCoverRequests,
      getAvailableShifts,
      getCurrentRotas,
      getRota,
      getRotas,
      getRotasAndShiftsForPeriod,
      getRotasExcludingLocked,
    };
  },
  {
    localStorageCache: [],
  },
);
